import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import { Eye, EyeSlash as EyeOff, MagnifyingGlass as Search, Buildings as Building2, ArrowLeft, TrendUp as TrendingUp, ShoppingBag, Users, ChartBar as BarChart3 } from '@phosphor-icons/react';
import { addRecentBusiness } from './PWAHome';

const Login = () => {
  const [currentTab, setCurrentTab] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBusinessSearch, setShowBusinessSearch] = useState(false); // For multiple accounts case
  const [businessSearch, setBusinessSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const indicatorRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    updateTabIndicator(currentTab);
    window.addEventListener('resize', () => updateTabIndicator(currentTab));
    return () => window.removeEventListener('resize', () => updateTabIndicator(currentTab));
  }, [currentTab]);

  // Debounced business search
  useEffect(() => {
    if (!showBusinessSearch || businessSearch.length < 3) {
      setSearchResults([]);
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await authAPI.searchBusinesses(username, password, businessSearch);
        setSearchResults(response.data.businesses || []);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [businessSearch, showBusinessSearch, username, password]);

  const updateTabIndicator = (tab) => {
    if (!tabsRef.current || !indicatorRef.current) return;
    const tabs = tabsRef.current.querySelectorAll('[data-tab]');
    const index = tab === 'login' ? 0 : 1;
    const tabWidth = tabs[0]?.offsetWidth || 0;
    indicatorRef.current.style.width = `${tabWidth}px`;
    indicatorRef.current.style.left = `${index * (tabWidth + 4) + 4}px`;
  };

  const switchTab = (tab) => {
    if (currentTab === tab) return;
    setError('');
    setCurrentTab(tab);
  };

  const handleLogin = async (e, tenantSlug = null) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password, tenantSlug);

    if (result.success) {
      const loggedInUser = result.user;
      setShowBusinessSearch(false); // Clear business selection

      // Redirect based on role and tenant
      if (loggedInUser?.isSuperAdmin) {
        addRecentBusiness('admin', loggedInUser.tenantName || 'Super Admin', { role: 'SUPER_ADMIN' });
        navigate('/admin/dashboard', { replace: true });
      } else if (loggedInUser?.tenantSlug) {
        const slug = loggedInUser.tenantSlug;
        addRecentBusiness(slug, loggedInUser.tenantName || slug, { businessType: loggedInUser.businessType, role: loggedInUser.role });
        if (loggedInUser.role === 'OWNER' || loggedInUser.role === 'ADMIN') {
          navigate(`/${slug}/owner/dashboard`, { replace: true });
        } else if (loggedInUser.role === 'MANAGER') {
          navigate(`/${slug}/manager/dashboard`, { replace: true });
        } else if (loggedInUser.role === 'KITCHEN') {
          navigate(`/${slug}/kitchen`, { replace: true });
        } else {
          navigate(`/${slug}/pos`, { replace: true });
        }
      } else {
        // Fallback - shouldn't happen but just in case
        setError('Login successful but unable to determine redirect. Please contact support.');
      }
    } else if (result.useBusinessLogin) {
      // Multiple accounts - show business search
      setError('');
      setShowBusinessSearch(true);
      setBusinessSearch('');
      setSearchResults([]);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Handle selecting a business from search results
  const handleSelectBusiness = async (slug) => {
    setLoading(true);
    setError('');

    const result = await login(username, password, slug);

    if (result.success) {
      const loggedInUser = result.user;
      setShowBusinessSearch(false);

      // Redirect based on role and tenant
      if (loggedInUser?.tenantSlug) {
        const userSlug = loggedInUser.tenantSlug;
        addRecentBusiness(userSlug, loggedInUser.tenantName || userSlug, { businessType: loggedInUser.businessType, role: loggedInUser.role });
        if (loggedInUser.role === 'OWNER' || loggedInUser.role === 'ADMIN') {
          navigate(`/${userSlug}/owner/dashboard`, { replace: true });
        } else if (loggedInUser.role === 'MANAGER') {
          navigate(`/${userSlug}/manager/dashboard`, { replace: true });
        } else if (loggedInUser.role === 'KITCHEN') {
          navigate(`/${userSlug}/kitchen`, { replace: true });
        } else {
          navigate(`/${userSlug}/pos`, { replace: true });
        }
      }
    } else {
      setError(result.error || 'Login failed');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F3EE] flex font-['Manrope'] safe-area-top safe-area-bottom">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* ━━━ LEFT BRANDED PANEL (desktop only) ━━━ */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-[#2D2A26] relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-transparent to-emerald-800/10" />

        {/* Decorative grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Top: Logo & tagline */}
          <div>
            <Link to="/" className="inline-flex items-center gap-3 mb-16 group no-underline">
              <img src="/images/logo.png" alt="SmartPOS Africa" className="w-11 h-11 rounded-2xl shadow-elevated shadow-emerald-900/30 group-hover:scale-105 transition-transform duration-200 bg-primaryB-50 p-1" />
              <span className="text-white/90 font-extrabold text-xl tracking-tight">Smart POS</span>
            </Link>

            <h1 className="text-2xl xl:text-3xl font-extrabold text-white leading-[1.1] tracking-tight mb-5">
              Run your business<br />
              <span className="text-emerald-400">smarter</span>.
            </h1>
            <p className="text-white/50 text-base xl:text-lg leading-relaxed max-w-md">
              Point of sale, inventory, expenses, and staff management – all in one place. Join hundreds of businesses already growing with Smart POS.
            </p>
          </div>

          {/* Middle: Floating stat cards */}
          <div className="flex-1 flex items-center justify-center py-10">
            <div className="relative w-full max-w-md h-64">
              {/* Card 1 - Today's sales */}
              <div className="absolute top-0 left-4 bg-primaryB-50/[0.08] backdrop-blur-md border border-white/[0.1] rounded-2xl px-5 py-4 shadow-2xl animate-[loginFloat1_6s_ease-in-out_infinite]">
                <div className="flex items-center gap-3 mb-1.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp weight="bold" className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-tight">GHS 2,450</p>
                    <p className="text-white/40 text-xs">Today's sales</p>
                  </div>
                </div>
              </div>

              {/* Card 2 - Items sold */}
              <div className="absolute top-2 right-0 bg-primaryB-50/[0.08] backdrop-blur-md border border-white/[0.1] rounded-2xl px-5 py-4 shadow-2xl animate-[loginFloat2_6s_ease-in-out_infinite_1s]">
                <div className="flex items-center gap-3 mb-1.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <ShoppingBag weight="bold" className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-tight">34 items sold</p>
                    <p className="text-white/40 text-xs">Since 9:00 AM</p>
                  </div>
                </div>
              </div>

              {/* Card 3 - Active staff */}
              <div className="absolute bottom-6 left-10 bg-primaryB-50/[0.08] backdrop-blur-md border border-white/[0.1] rounded-2xl px-5 py-4 shadow-2xl animate-[loginFloat3_6s_ease-in-out_infinite_2s]">
                <div className="flex items-center gap-3 mb-1.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Users weight="bold" className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-tight">6 staff online</p>
                    <p className="text-white/40 text-xs">Across 3 branches</p>
                  </div>
                </div>
              </div>

              {/* Card 4 - Revenue trend */}
              <div className="absolute bottom-0 right-6 bg-primaryB-50/[0.08] backdrop-blur-md border border-white/[0.1] rounded-2xl px-5 py-4 shadow-2xl animate-[loginFloat1_6s_ease-in-out_infinite_3s]">
                <div className="flex items-center gap-3 mb-1.5">
                  <div className="w-8 h-8 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <BarChart3 weight="bold" className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-tight">+18% revenue</p>
                    <p className="text-white/40 text-xs">vs. last month</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Trust text */}
          <div className="flex items-center gap-6">
            <div className="flex -space-x-2">
              {['bg-emerald-500', 'bg-amber-500', 'bg-blue-500', 'bg-purple-500'].map((bg, i) => (
                <div key={i} className={`w-8 h-8 rounded-full ${bg} border-2 border-[#2D2A26] flex items-center justify-center`}>
                  <span className="text-white text-[10px] font-bold">{['A', 'K', 'M', 'J'][i]}</span>
                </div>
              ))}
            </div>
            <p className="text-white/40 text-sm">
              Trusted by <span className="text-white/70 font-semibold">500+</span> businesses in Ghana
            </p>
          </div>
        </div>
      </div>

      {/* ━━━ RIGHT SIDE: FORM ━━━ */}
      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10 sm:py-12">
        <div className="w-full max-w-[440px]">
          {/* Mobile header (hidden on desktop since left panel has logo) */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src="/images/logo.png" alt="SmartPOS Africa" className="w-10 h-10 rounded-2xl" />
            <span className="text-[#2D2A26] font-extrabold text-xl">Smart POS</span>
          </div>

          {/* Form Card */}
          <div className="bg-primaryB-50 rounded-2xl border border-[#E5E1DA] shadow-[0_8px_40px_-12px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] p-7 sm:p-9">
            {/* Back to Home */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-medium mb-5 transition-colors text-sm no-underline"
            >
              <ArrowLeft weight="bold" className="w-4 h-4" />
              Back to Home
            </Link>

            {/* Welcome text */}
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#2D2A26] mb-1">Welcome back</h2>
              <p className="text-[#6B675F] text-sm">Sign in to manage your business</p>
            </div>

            {/* Tabs */}
            <div
              ref={tabsRef}
              className="relative flex bg-[#F5F3EE] rounded-2xl p-1 mb-6"
            >
              <div
                ref={indicatorRef}
                className="absolute top-1 h-[calc(100%-8px)] bg-primaryB-50 rounded-xl transition-all duration-300 ease-in-out"
              />
              <button
                data-tab="login"
                className={`relative z-10 flex-1 py-2.5 text-sm font-bold rounded-xl transition-colors duration-200 ${
                  currentTab === 'login'
                    ? 'text-[#2D2A26]'
                    : 'text-[#ADA99F] hover:text-[#6B675F]'
                }`}
                onClick={() => switchTab('login')}
                type="button"
              >
                Sign In
              </button>
              <button
                data-tab="signup"
                className={`relative z-10 flex-1 py-2.5 text-sm font-bold rounded-xl transition-colors duration-200 ${
                  currentTab === 'signup'
                    ? 'text-[#2D2A26]'
                    : 'text-[#ADA99F] hover:text-[#6B675F]'
                }`}
                onClick={() => switchTab('signup')}
                type="button"
              >
                Sign Up
              </button>
            </div>

            {/* Forms with transition wrapper */}
            <div className="relative">
              {/* Business Search - shown when multiple accounts found */}
              {currentTab === 'login' && showBusinessSearch && (
                <div className="animate-[fadeSlideIn_0.3s_ease-out]">
                  <div className="text-center mb-5">
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Building2 weight="bold" size={28} className="text-emerald-700" />
                    </div>
                    <h3 className="text-base font-bold text-[#2D2A26] mb-2">
                      Multiple Accounts Found
                    </h3>
                    <p className="text-[13px] text-[#6B675F] leading-relaxed">
                      Search for your business to continue
                    </p>
                  </div>

                  {/* Search Input */}
                  <div className="relative mb-4">
                    <Search weight="bold"
                      size={18}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ADA99F]"
                    />
                    <input
                      type="text"
                      placeholder="Type your business name..."
                      value={businessSearch}
                      onChange={(e) => setBusinessSearch(e.target.value)}
                      autoFocus
                      className="w-full py-3.5 pl-11 pr-3.5 rounded-2xl border-2 border-[#E5E1DA] bg-[#FAF9F6] text-[#2D2A26] text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all duration-200 placeholder:text-[#ADA99F]"
                    />
                  </div>

                  {/* Search Results */}
                  <div className="min-h-[120px]">
                    {businessSearch.length > 0 && businessSearch.length < 3 && (
                      <p className="text-center text-[13px] text-[#6B675F] py-5">
                        Type at least 3 characters to search
                      </p>
                    )}

                    {searching && (
                      <p className="text-center text-[13px] text-[#6B675F] py-5">
                        Searching...
                      </p>
                    )}

                    {!searching && businessSearch.length >= 3 && searchResults.length === 0 && (
                      <p className="text-center text-[13px] text-[#6B675F] py-5">
                        No matching business found
                      </p>
                    )}

                    {!searching && searchResults.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {searchResults.map((biz) => (
                          <button
                            key={biz.slug}
                            onClick={() => handleSelectBusiness(biz.slug)}
                            disabled={loading}
                            className="p-3.5 rounded-2xl border-2 border-[#E5E1DA] bg-[#FAF9F6] text-[#2D2A26] text-left font-semibold text-sm transition-all duration-200 hover:border-emerald-600 hover:bg-emerald-50 disabled:opacity-70 disabled:cursor-not-allowed"
                          >
                            {biz.businessName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Back Button */}
                  <button
                    onClick={() => {
                      setShowBusinessSearch(false);
                      setBusinessSearch('');
                      setSearchResults([]);
                      setError('');
                    }}
                    className="mt-4 w-full py-3 rounded-xl text-[13px] text-[#6B675F] hover:text-[#2D2A26] bg-transparent border-none cursor-pointer transition-colors duration-200"
                  >
                    &larr; Back to login
                  </button>
                </div>
              )}

              {/* Login Form */}
              {currentTab === 'login' && !showBusinessSearch && (
                <form onSubmit={handleLogin} className="animate-[fadeSlideIn_0.3s_ease-out]">
                  {error && (
                    <div className="mb-4 p-3.5 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium flex items-start gap-2">
                      <span className="shrink-0 mt-0.5">!</span>
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="mb-4">
                    <label
                      htmlFor="username"
                      className="block text-sm font-semibold text-[#2D2A26] mb-2"
                    >
                      Username or Email
                    </label>
                    <input
                      type="text"
                      id="username"
                      placeholder="Enter your username or email"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoFocus
                      className="w-full py-3.5 px-4 rounded-2xl border-2 border-[#E5E1DA] bg-[#FAF9F6] text-[#2D2A26] text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all duration-200 placeholder:text-[#ADA99F]"
                    />
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="password"
                      className="block text-sm font-semibold text-[#2D2A26] mb-2"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full py-3.5 px-4 pr-12 rounded-2xl border-2 border-[#E5E1DA] bg-[#FAF9F6] text-[#2D2A26] text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all duration-200 placeholder:text-[#ADA99F]"
                      />
                      <button
                        type="button"
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#ADA99F] hover:text-[#6B675F] transition-colors duration-200 bg-transparent border-none cursor-pointer p-1"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff weight="bold" size={18} /> : <Eye weight="bold" size={18} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 bg-emerald-700 hover:bg-emerald-800 active:scale-[0.98] text-white rounded-full font-bold text-sm transition-all duration-200 shadow-elevated shadow-emerald-700/20 disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </button>

                  <div className="mt-6 text-center space-y-2.5">
                    <p>
                      <Link
                        to="/find-business"
                        className="text-[13px] text-emerald-700 hover:text-emerald-800 font-medium transition-colors duration-200 no-underline"
                      >
                        Can't login? Find your business
                      </Link>
                    </p>
                    <p>
                      <a
                        href="https://wa.me/233559306229"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[13px] text-[#ADA99F] hover:text-[#6B675F] transition-colors duration-200"
                      >
                        Need help? Chat with us on WhatsApp
                      </a>
                    </p>
                  </div>
                </form>
              )}

              {/* Signup Tab Content */}
              {currentTab === 'signup' && (
                <div className="animate-[fadeSlideIn_0.3s_ease-out]">
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Building2 weight="bold" size={28} className="text-emerald-700" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2D2A26] mb-2">Register your business</h3>
                    <p className="text-[#6B675F] text-sm leading-relaxed">
                      Get started with Smart POS. Our team will review your application and set up your account.
                    </p>
                  </div>

                  <Link
                    to="/signup"
                    className="block w-full py-3.5 bg-emerald-700 hover:bg-emerald-800 active:scale-[0.98] text-white rounded-full font-bold text-sm text-center transition-all duration-200 shadow-elevated shadow-emerald-700/20 no-underline"
                  >
                    Start Registration
                  </Link>

                  <div className="mt-6 text-center">
                    <p className="text-[13px] text-[#6B675F] leading-relaxed">
                      Already submitted? We'll email you when it's reviewed.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer text below card */}
          <p className="text-center text-[#ADA99F] text-xs mt-6">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-[#6B675F] hover:text-[#2D2A26] underline transition-colors">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#6B675F] hover:text-[#2D2A26] underline transition-colors">Privacy Policy</Link>
          </p>
        </div>
      </div>

      {/* Keyframe animations injected via style tag */}
      <style>{`
        @keyframes loginFloat1 {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes loginFloat2 {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes loginFloat3 {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Login;
